import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export function ForcedPasswordChangeDialog() {
  const { user, refreshMe, logout } = useAuth();
  const open = !!(user && user.must_change_password);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Mindestens 8 Zeichen"); return; }
    if (pw !== pw2) { toast.error("Passwörter stimmen nicht überein"); return; }
    setBusy(true);
    try {
      await api.patch("/auth/me", { password: pw });
      toast.success("Passwort erfolgreich geändert");
      await refreshMe?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fehler beim Speichern");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* nicht schließbar */ }}>
      <DialogContent
        className="max-w-md"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="forced-pw-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight text-xl flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" /> Passwort ändern erforderlich
          </DialogTitle>
          <DialogDescription>
            Sie melden sich zum ersten Mal an. Bitte legen Sie jetzt ein neues, persönliches Passwort fest — das Initialpasswort verliert danach seine Gültigkeit.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="new-pw">Neues Passwort <span className="text-xs text-muted-foreground">(mind. 8 Zeichen)</span></Label>
            <Input id="new-pw" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} data-testid="forced-pw-new" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-pw2">Neues Passwort bestätigen</Label>
            <Input id="new-pw2" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8} data-testid="forced-pw-confirm" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={logout} data-testid="forced-pw-logout">Abmelden</Button>
            <Button type="submit" disabled={busy} data-testid="forced-pw-submit">
              <KeyRound className="h-4 w-4 mr-2" /> {busy ? "Speichere …" : "Passwort setzen"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
